import { useQuery } from '@tanstack/react-query';
import { Activity, Droplets, Gauge, Thermometer } from 'lucide-react';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import { adminApi, type SensorSnapshot } from '../../services/adminApi';

const formatRelative = (iso: string | null) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'in the future';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const FreshnessBadge = ({ iso }: { iso: string | null }) => {
  if (!iso) return <span className="text-xs text-slate-400">—</span>;
  const ms = Date.now() - new Date(iso).getTime();
  const stale = ms > 48 * 60 * 60 * 1000;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
        stale
          ? 'bg-amber-50 text-amber-700 ring-amber-200'
          : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      }`}
    >
      {formatRelative(iso)}
    </span>
  );
};

const AdminSensorsView = () => {
  const q = useQuery({
    queryKey: ['admin', 'sensors'],
    queryFn: async () => (await adminApi.sensorsSnapshot()).data,
    refetchInterval: 30_000,
  });

  const snapshots = q.data ?? [];

  return (
    <>
      <PageHeader
        title="Sensor snapshot"
        subtitle="Latest reading per machine. Auto-refreshes every 30s."
      />

      <Card title={`Per-machine snapshot · ${snapshots.length}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Machine</th>
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 pr-4 font-medium">Insight ts</th>
                <th className="py-2 pr-4 font-medium">Totes</th>
                <th className="py-2 pr-4 font-medium">Kgs</th>
                <th className="py-2 pr-4 font-medium">Pile temp</th>
                <th className="py-2 pr-4 font-medium">Moisture</th>
                <th className="py-2 pr-4 font-medium">Async ts</th>
                <th className="py-2 pr-4 font-medium">Pile level</th>
                <th className="py-2 pr-4 font-medium">Load cell</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {snapshots.map((s) => (
                <SnapshotRow key={s.machineUid} s={s} />
              ))}
              {snapshots.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-xs text-slate-400">
                    {q.isLoading ? 'Loading…' : 'No machines.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <LegendCard Icon={Activity} label="Insight latency" hint="Most recent daily insight per machine." />
        <LegendCard Icon={Thermometer} label="Pile temp" hint="From DailyComposterInsight.pileTemp. Empty for lifters." />
        <LegendCard Icon={Droplets} label="Moisture" hint="DailyComposterInsight.avgMoisture, percent." />
        <LegendCard Icon={Gauge} label="Async events" hint="Live sensor stream (machine status, weight)." />
      </section>
    </>
  );
};

const SnapshotRow = ({ s }: { s: SensorSnapshot }) => (
  <tr className="hover:bg-slate-50">
    <td className="py-2.5 pr-4 font-mono text-xs font-medium text-slate-800">{s.machineId ?? '—'}</td>
    <td className="py-2.5 pr-4">
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
          s.machineType === 'composter'
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            : 'bg-sky-50 text-sky-700 ring-sky-200'
        }`}
      >
        {s.machineType}
      </span>
    </td>
    <td className="py-2.5 pr-4">
      <FreshnessBadge iso={s.latestInsight?.ts ?? null} />
    </td>
    <td className="py-2.5 pr-4 font-mono text-xs">{s.latestInsight?.totesLoaded ?? '—'}</td>
    <td className="py-2.5 pr-4 font-mono text-xs">
      {s.latestInsight ? s.latestInsight.kgsLoaded.toFixed(0) : '—'}
    </td>
    <td className="py-2.5 pr-4 font-mono text-xs">
      {s.latestInsight?.pileTemp != null ? `${s.latestInsight.pileTemp.toFixed(1)}°C` : '—'}
    </td>
    <td className="py-2.5 pr-4 font-mono text-xs">
      {s.latestInsight?.avgMoisture != null ? `${s.latestInsight.avgMoisture.toFixed(0)}%` : '—'}
    </td>
    <td className="py-2.5 pr-4">
      <FreshnessBadge iso={s.latestAsync?.timestampUtc ?? null} />
    </td>
    <td className="py-2.5 pr-4 font-mono text-xs">
      {s.latestAsync?.pileLevelCm != null ? `${s.latestAsync.pileLevelCm.toFixed(0)} cm` : '—'}
    </td>
    <td className="py-2.5 pr-4 font-mono text-xs">
      {s.latestAsync?.loadCellKg != null ? `${s.latestAsync.loadCellKg.toFixed(0)} kg` : '—'}
    </td>
  </tr>
);

const LegendCard = ({
  Icon,
  label,
  hint,
}: {
  Icon: typeof Activity;
  label: string;
  hint: string;
}) => (
  <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <Icon className="size-5 shrink-0 text-tfs-primary" />
    <div>
      <p className="text-sm font-medium text-slate-800">{label}</p>
      <p className="text-xs text-slate-500">{hint}</p>
    </div>
  </div>
);

export default AdminSensorsView;
